import type { ReactNode } from "react";
import { Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { CONTACT, FULL_ADDRESS } from "@/data/contact";
import { OPEN_COOKIE_SETTINGS } from "@/lib/analytics";

// Update this whenever the policy text changes, and bump the page's
// <lastmod> in public/sitemap.xml.
const LAST_UPDATED = "15 October 2026";

const linkClass = "font-semibold text-accent hover:underline underline-offset-4";

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
      {children}
    </a>
  );
}

const Email = () => (
  <a href={`mailto:${CONTACT.email}`} className={linkClass}>
    {CONTACT.email}
  </a>
);

const CookieSettingsLink = () => (
  <button
    type="button"
    onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS))}
    className={linkClass}
  >
    Cookie settings
  </button>
);

const SECTIONS: { id: string; title: string; body: ReactNode }[] = [
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          This website, www.hbtintegrisys.com, is run by HBT Integrisys, {FULL_ADDRESS} ("HBT
          Integrisys", "we", "us"). We decide how the personal data collected through this website
          is used, and we are responsible for it.
        </p>
        <p>
          This policy explains what we collect when you visit the website or contact us through it,
          why we collect it, who we share it with and the choices you have. It is written with
          India's Digital Personal Data Protection Act, 2023 in mind.
        </p>
      </>
    ),
  },
  {
    id: "information-you-give-us",
    title: "Information you give us",
    body: (
      <>
        <p>We only collect information you choose to send us, such as when you:</p>
        <ul>
          <li>
            fill in a contact or enquiry form on this website, including the enquiry form on the{" "}
            <Link href="/pricing" className={linkClass}>pricing page</Link> — your name, email
            address, phone number, the plan you selected and your message;
          </li>
          <li>email us, call us or message us on WhatsApp;</li>
          <li>
            apply for an internship through our Google Form, or email your resume for a job
            opening.
          </li>
        </ul>
        <p>
          Please don't include sensitive information, such as financial or health details, in a
          form or message.
        </p>
      </>
    ),
  },
  {
    id: "google-analytics",
    title: "Website analytics (Google Analytics)",
    body: (
      <>
        <p>
          With your permission, we use Google Analytics, a free tool from Google, to see how people
          use our website — for example, which pages are popular and where visitors come from. This
          helps us improve the website. It tells us things like:
        </p>
        <ul>
          <li>which pages you visit, how long you stay and what you click;</li>
          <li>how you found us, for example through Google search or another website;</li>
          <li>the type of device and browser you use;</li>
          <li>your approximate location, such as your city or country.</li>
        </ul>
        <p>
          We see this only as overall numbers, such as "200 visits to the pricing page this week".
          It doesn't tell us your name, phone number or email address, and we don't use it to work
          out who you are.
        </p>
        <p>
          <strong>We only use Google Analytics if you agree.</strong> When you first visit, a
          banner asks whether you accept. Until you click Accept, Google Analytics is switched off
          and nothing is sent to Google. If you accept, Google Analytics saves a small file (a
          "cookie") in your browser so it can recognise repeat visits; it expires after two years
          unless you delete it sooner.
        </p>
        <p>
          Google may store this information on servers outside India. You can read{" "}
          <External href="https://policies.google.com/technologies/partner-sites">
            how Google uses this information
          </External>{" "}
          in{" "}
          <External href="https://policies.google.com/privacy">Google's Privacy Policy</External>.
        </p>
        <p>
          <strong>Changing your mind.</strong> You can accept or decline at any time using the{" "}
          <CookieSettingsLink /> link at the bottom of every page. If you decline, we switch Google
          Analytics off and remove its cookies from your browser. The website works the same
          either way.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use-information",
    title: "How we use your information",
    body: (
      <>
        <p>We use the information described above to:</p>
        <ul>
          <li>reply to your enquiry and send you the quotation, proposal or details you asked for;</li>
          <li>provide the services you engage us for;</li>
          <li>review job and internship applications;</li>
          <li>understand which pages are useful and improve the website's content and performance;</li>
          <li>keep the website secure and meet our legal obligations.</li>
        </ul>
        <p>
          We do not sell your personal data, and we do not send you marketing messages unless you
          have asked us to.
        </p>
      </>
    ),
  },
  {
    id: "consent",
    title: "Your consent",
    body: (
      <p>
        By submitting a form or contacting us, you agree to us using the details you provide to
        respond to you. We use Google Analytics only if you accept it in our cookie banner. You can
        withdraw your consent at any time — decline analytics from <CookieSettingsLink />, or
        email us at <Email /> to stop us using your details.
        Withdrawing consent does not affect anything we did before you withdrew it.
      </p>
    ),
  },
  {
    id: "sharing",
    title: "Who we share information with",
    body: (
      <>
        <p>
          We share personal data only with service providers that help us run this website, and
          only as needed for them to provide their service:
        </p>
        <ul>
          <li><strong>Google</strong> — Google Analytics (website analytics) and Google Forms (internship applications);</li>
          <li><strong>EmailJS</strong> — delivers the messages you send through our website forms to our inbox;</li>
          <li><strong>Hostinger</strong> — hosts this website; its servers keep standard access logs, such as IP addresses and requested pages, for security and operations.</li>
        </ul>
        <p>
          These providers may store data outside India. We may also disclose information when
          required by law, or to protect our rights, our users or the public.
        </p>
      </>
    ),
  },
  {
    id: "third-party-content",
    title: "Third-party content and links",
    body: (
      <p>
        Our contact page includes an embedded Google Map, which Google may use to set its own
        cookies when it loads. The website also links to WhatsApp, LinkedIn and Instagram. When you
        use these services or follow links to other websites, their own privacy policies apply, and
        we are not responsible for their practices.
      </p>
    ),
  },
  {
    id: "retention",
    title: "How long we keep information",
    body: (
      <p>
        We keep enquiries and the related correspondence for as long as needed to respond to you and
        provide our services, and afterwards only as long as required for legal, accounting or
        record-keeping purposes. Job and internship applications are kept for as long as needed to
        consider you for current or future openings, unless you ask us to delete them. Google
        Analytics data is kept for the retention period set in our Google Analytics account, after
        which Google deletes it automatically.
      </p>
    ),
  },
  {
    id: "security",
    title: "Security",
    body: (
      <p>
        This website is served over HTTPS, and we take reasonable technical and organisational
        measures to protect the personal data we hold. No method of transmission over the internet
        or of electronic storage is completely secure, however, so we cannot guarantee absolute
        security.
      </p>
    ),
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: (
      <>
        <p>Subject to applicable law, you have the right to:</p>
        <ul>
          <li>ask what personal data we hold about you and how we use it;</li>
          <li>ask us to correct, complete or update your personal data;</li>
          <li>ask us to delete your personal data;</li>
          <li>withdraw your consent at any time;</li>
          <li>nominate another person to exercise these rights on your behalf;</li>
          <li>raise a grievance with us, and if it isn't resolved, with the Data Protection Board of India.</li>
        </ul>
        <p>
          To exercise any of these rights, email <Email />. We may ask you to confirm your identity
          before acting on your request, and we'll respond within a reasonable time.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children",
    body: (
      <p>
        This website is intended for businesses and adults. We do not knowingly collect personal
        data from anyone under 18. If you believe a child has sent us their information, please
        contact us and we will delete it.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: (
      <p>
        We may update this policy from time to time, for example when we add a new service to the
        website. The "Last updated" date at the top shows when it last changed.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact us",
    body: (
      <p>
        For any questions or grievances about this policy or your personal data, email{" "}
        <Email />, call{" "}
        <a href={CONTACT.phoneHref} className={linkClass}>{CONTACT.phone}</a>, or write to HBT
        Integrisys, {FULL_ADDRESS}.
      </p>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Seo
        title="Privacy Policy"
        description="How HBT Integrisys collects and uses personal data on its website, including Google Analytics, cookies, enquiry forms and your rights under Indian law."
        keywords={[
          "HBT Integrisys privacy policy",
          "Google Analytics cookie policy",
          "website privacy policy India",
        ]}
        path="/privacy-policy"
      />
      <Navbar />

      <main className="flex-1">
        <PageHero
          eyebrow="Legal"
          title="Privacy Policy"
          description="How we collect, use and protect your information when you visit our website or contact us."
        />

        <section className="py-16 md:py-20 bg-background">
          <div className="mx-auto max-w-[1280px] px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              <p className="text-sm text-muted-foreground mb-10">Last updated: {LAST_UPDATED}</p>

              <nav aria-label="On this page" className="mb-12 rounded-[20px] border border-border/60 bg-muted p-6">
                <h2 className="font-display font-semibold text-foreground mb-3">On this page</h2>
                <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm list-decimal pl-5 marker:text-muted-foreground">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`} className="text-foreground/80 hover:text-accent transition-colors">
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>

              <div className="space-y-12">
                {SECTIONS.map((s, idx) => (
                  <section key={s.id} id={s.id} className="scroll-mt-24">
                    <h2 className="text-2xl font-display font-semibold tracking-[-0.03em] text-foreground mb-4">
                      {idx + 1}. {s.title}
                    </h2>
                    <div className="space-y-4 text-foreground/80 leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-foreground">
                      {s.body}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
